// components/Modal/RefundActionModal.js
import React, { useState } from 'react';
import styled from 'styled-components';
import WarningOutlineIcon from '../Icons/WarningOutlineIcon';
import Button from '../Button/Button';
import Modal from './Modal';
import InputTextArea from '../Input/InputTextArea';
import InputText from '../Input/InputText';
import Typography from '../Typography';
import ToogableTabs from '../Toogle/ToogableTabs';
import { theme } from '../../theme';
import { useTranslation } from 'react-i18next';
import { TICKET_PAID } from '../../constants/eventTypes';
import { formatPrice } from '../../utilities';


const RefundActionModal = ({
  isOpen,
  onClose,
  attendee,
  actionType, // 'accept' or 'deny'
  onConfirm,
  isLoading = false
}) => {
  const { t } = useTranslation('manageEventAttendees');
  const [organizerMessage, setOrganizerMessage] = useState('');
  const [refundMode, setRefundMode] = useState('full');
  const [partialRefundAmount, setPartialRefundAmount] = useState('');
  const [validationError, setValidationError] = useState('');

  // Limpiar el mensaje cuando el modal se cierre
  React.useEffect(() => {
    if (!isOpen) {
      setOrganizerMessage('');
      setRefundMode('full');
      setPartialRefundAmount('');
      setValidationError('');
    }
  }, [isOpen]);

  if (!isOpen || !attendee) return null;

  const ticketPrice = Number(attendee?.ticketId?.price || 0);
  const fullRefundAmountCents = Math.round(ticketPrice * 100);
  const formattedTicketPrice = formatPrice(ticketPrice);

  const handleConfirm = (e) => {
    e.preventDefault();

    let refundAmountCents = null;

    if (isAcceptAction && isPaidTicket && refundMode === 'partial') {
      const normalizedAmount = Number(String(partialRefundAmount).replace(',', '.'));

      if (!Number.isFinite(normalizedAmount)) {
        setValidationError(t('refundActionModal.validation.invalidAmount'));
        return;
      }

      refundAmountCents = Math.round(normalizedAmount * 100);

      if (refundAmountCents <= 0) {
        setValidationError(t('refundActionModal.validation.minAmount'));
        return;
      }

      if (refundAmountCents > fullRefundAmountCents) {
        setValidationError(t('refundActionModal.validation.maxAmount', {
          amount: formattedTicketPrice
        }));
        return;
      }
    }

    setValidationError('');
    onConfirm({
      organizerMessage,
      refundAmountCents
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleConfirm(e);
  };

  const handleClose = () => {
    setOrganizerMessage('');
    setRefundMode('full');
    setPartialRefundAmount('');
    setValidationError('');
    onClose();
  };

  const isAcceptAction = actionType === 'accept';
  const isPaidTicket = attendee?.ticketId?.type === TICKET_PAID;

  const getTitle = () => {
    // return isAcceptAction ? 'Aceptar solicitud de reembolso' : 'Denegar solicitud de reembolso';
    return isAcceptAction ? t('refundActionModal.title.accept') : t('refundActionModal.title.deny');

  };

  const getWarningMessage = () => {
    if (isAcceptAction && isPaidTicket) {
      return (
        <WarningMessage>
          <WarningTitle>
            <WarningOutlineIcon width={20} height={20} fill={theme.colors.warningMain} />
            <Typography as="p" color={theme.colors.warningMain} $variant="body-1-semibold" $variantTablet="body-2-semibold" $variantMobile="body-2-semibold">
              {/* Ticket de pago */}
              {t('refundActionModal.refundInfo.warningTitle')}
            </Typography>
          </WarningTitle>
          <Typography as="p" color={theme.colors.defaultStrong} $variant="body-2-regular">
            {t('refundActionModal.refundInfo.warningDescription')}
          </Typography>
          <Typography as="p" color={theme.colors.defaultStrong} $variant="body-2-regular" style={{ marginTop: '8px' }}>
            {t('refundActionModal.refundInfo.warningSecondary')}
          </Typography>
        </WarningMessage>
      );
    }
    return null;
  };

  const getConfirmButtonText = () => {
    // return isAcceptAction ? 'Aceptar reembolso' : 'Denegar reembolso';
    return isAcceptAction ? t('refundActionModal.buttons.accept') : t('refundActionModal.buttons.deny');

  };

  return (
    <Modal onClose={handleClose} title={getTitle()}>
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <AttendeeInfo>
            <InfoRow>
              <Typography as="span" $variant="body-2-medium" color={theme.colors.defaultStrong}>
                {t('refundActionModal.attendeeInfo.name')}
              </Typography>
              <Typography as="span" $variant="body-2-regular" color={theme.colors.defaultWeak}>
                {attendee?.userId?.name || 'Usuario'} {attendee?.userId?.lastName || ''}
              </Typography>
            </InfoRow>
            <InfoRow>
              <Typography as="span" $variant="body-2-medium" color={theme.colors.defaultStrong}>
                {t('refundActionModal.attendeeInfo.ticket')}
              </Typography>
              <Typography as="span" $variant="body-2-regular" color={theme.colors.defaultWeak}>
                {attendee?.ticketId?.name}
              </Typography>
            </InfoRow>
            {isPaidTicket && (
              <>
                <InfoRow>
                  <Typography as="span" $variant="body-2-medium" color={theme.colors.defaultStrong}>
                    {t('refundActionModal.refundInfo.originalAmount')}
                  </Typography>
                  <Typography as="span" $variant="body-2-regular" color={theme.colors.defaultWeak}>
                    {formattedTicketPrice}
                  </Typography>
                </InfoRow>
                <InfoRow>
                  <Typography as="span" $variant="body-2-medium" color={theme.colors.defaultStrong}>
                    {t('refundActionModal.refundInfo.maxAmount')}
                  </Typography>
                  <Typography as="span" $variant="body-2-regular" color={theme.colors.defaultWeak}>
                    {formattedTicketPrice}
                  </Typography>
                </InfoRow>
              </>
            )}
          </AttendeeInfo>

          {getWarningMessage()}

          {isAcceptAction && isPaidTicket && (
            <RefundOptionsSection>
              <Label as="div">
                <Typography as="span" $variant="body-2-medium" color={theme.colors.defaultStrong}>
                  {t('refundActionModal.refundInfo.typeLabel')}
                </Typography>
              </Label>
              <ToogableTabs
                options={[
                  { label: t('refundActionModal.refundInfo.typeOptions.full'), value: 'full' },
                  { label: t('refundActionModal.refundInfo.typeOptions.partial'), value: 'partial' },
                ]}
                activeOption={refundMode}
                onTabChange={(value) => {
                  setRefundMode(value);
                  setValidationError('');
                }}
              />

              {refundMode === 'partial' && (
                <RefundAmountField>
                  <Label htmlFor="partialRefundAmount">
                    <Typography as="span" $variant="body-2-medium" color={theme.colors.defaultStrong}>
                      {t('refundActionModal.refundInfo.partialAmountLabel')}
                    </Typography>
                  </Label>
                  <InputText
                    id="partialRefundAmount"
                    type="number"
                    min="0.01"
                    max={ticketPrice}
                    step="0.01"
                    inputMode="decimal"
                    value={partialRefundAmount}
                    onChange={(e) => {
                      setPartialRefundAmount(e.target.value);
                      setValidationError('');
                    }}
                    placeholder={t('refundActionModal.refundInfo.partialAmountPlaceholder')}
                    $variant={validationError ? 'error' : 'default'}
                  />
                  <HelperText>
                    <Typography as="p" $variant="body-3-regular" color={theme.colors.defaultWeak}>
                      {t('refundActionModal.refundInfo.partialAmountHelp', {
                        amount: formattedTicketPrice
                      })}
                    </Typography>
                  </HelperText>
                  {validationError ? (
                    <ValidationError>
                      <Typography as="p" $variant="body-3-medium" color={theme.colors.errorMain}>
                        {validationError}
                      </Typography>
                    </ValidationError>
                  ) : null}
                </RefundAmountField>
              )}
            </RefundOptionsSection>
          )}

          <MessageSection>
            <Label htmlFor="organizerMessage">
              {/* Mensaje para el asistente (opcional) */}
              <Typography as="span" $variant="body-2-medium" color={theme.colors.defaultStrong}>
                {t('refundActionModal.organizerMessage.label')}
              </Typography>
            </Label>

            <InputTextArea
              style={{ minHeight: '120px', resize: 'vertical' }}
              id="organizerMessage"
              value={organizerMessage}
              onChange={(e) => setOrganizerMessage(e.target.value)}
              placeholder={t('refundActionModal.organizerMessage.placeholder')}
            />
          </MessageSection>
        </ModalBody>

        <ModalFooter>
          <Button
            $variant="ghost"
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            size="l"
            sizeTablet="l"
            sizeMobile="m"
          >
            {t('refundActionModal.buttons.cancel')}

          </Button>
          <Button
            $variant={isAcceptAction ? 'default' : 'defaultDanger'}
            type="submit"
            disabled={isLoading}
            size="l"
            sizeTablet="l"
            sizeMobile="m"
          >
            {isLoading ? t('refundActionModal.buttons.processing') : getConfirmButtonText()}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default RefundActionModal;



const ModalBody = styled.div`
  padding: ${({ theme }) => theme.sizing.sm};
`;

const AttendeeInfo = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  padding: ${({ theme }) => theme.sizing.sm};
  border-radius: ${({ theme }) => theme.radius.xs};
  margin-bottom: ${({ theme }) => theme.sizing.sm};
  gap: ${({ theme }) => theme.sizing.xs};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: ${({ theme }) => theme.radius.sms};
  
  &:last-child {
    padding-bottom: 0px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: ${({ theme }) => theme.sizing.xxs};
    flex-basis: 100%;
  }
`;

const WarningMessage = styled.div`
  background-color: ${({ theme }) => theme.fill.warningAlphaMain16};
  border-radius: ${({ theme }) => theme.radius.xs};
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.sizing.md};
  padding: ${({ theme }) => theme.sizing.sm};
`;

const WarningTitle = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.sizing.xxs};
  padding-bottom: ${({ theme }) => theme.sizing.xs};
`;

const MessageSection = styled.div`
  margin-bottom: ${({ theme }) => theme.sizing.sm};
`;

const RefundOptionsSection = styled.div`
  margin-bottom: ${({ theme }) => theme.sizing.md};
`;

const Label = styled.label`
  color: #1a1a1a;
  display: block;
  font-weight: 500;
  margin-bottom: ${({ theme }) => theme.sizing.xs};
`;

const RefundAmountField = styled.div`
  margin-top: ${({ theme }) => theme.sizing.xs};
`;

const HelperText = styled.div`
  margin-top: ${({ theme }) => theme.sizing.xs};
`;

const ValidationError = styled.div`
  margin-top: ${({ theme }) => theme.sizing.xs};
`;



const ModalFooter = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: space-between;
  padding: ${({ theme }) => theme.sizing.sm};
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  background: ${({ theme }) => theme.fill.defaultMain};
  align-self: stretch;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-wrap: wrap;
  }
`;
